import React, { useState, useRef, useCallback, useEffect, useImperativeHandle, forwardRef } from "react";

const SG = "'Space Grotesk', sans-serif";

// Duplicated from DiagnosticMapWindow rather than imported: that module imports
// THIS one, so sharing it the other way would close an import cycle.
// navigator.clipboard is undefined on non-secure origins (plain-http staging)
// and writeText rejects when the tab is not focused, hence the legacy fallback.
const copyTextToClipboard = async (text) => {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch (_) { /* fall through to the legacy path */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_) { return false; }
};

const renderShape = (shapeType, W, H, fill, stroke, sw, sd) => {
  const hw = W / 2, hh = H / 2;
  switch (shapeType) {
    case "rect":
      return <rect x={-hw} y={-hh} width={W} height={H} rx={6} ry={6} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={sd} />;
    case "roundedRect":
      return <rect x={-hw} y={-hh} width={W} height={H} rx={12} ry={12} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={sd} />;
    case "stadium":
      return <rect x={-hw} y={-hh} width={W} height={H} rx={hh} ry={hh} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={sd} />;
    case "diamond":
      return <polygon points={`0,${-hh} ${hw},0 0,${hh} ${-hw},0`} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={sd} />;
    case "hexagon": {
      const i = Math.min(18, hw * 0.25);
      return <polygon points={`${-hw},0 ${-hw+i},${-hh} ${hw-i},${-hh} ${hw},0 ${hw-i},${hh} ${-hw+i},${hh}`} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={sd} />;
    }
    case "parallelogram": {
      const sk = Math.min(14, hw * 0.18);
      return <polygon points={`${-hw+sk},${-hh} ${hw},${-hh} ${hw-sk},${hh} ${-hw},${hh}`} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={sd} />;
    }
    case "trapezoid": {
      const sk = Math.min(14, hw * 0.18);
      return <polygon points={`${-hw+sk},${-hh} ${hw-sk},${-hh} ${hw},${hh} ${-hw},${hh}`} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={sd} />;
    }
    case "subroutine": {
      const ins = Math.min(10, hw * 0.12);
      return (
        <>
          <rect x={-hw} y={-hh} width={W} height={H} rx={4} ry={4} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={sd} />
          <line x1={-hw+ins} y1={-hh} x2={-hw+ins} y2={hh} stroke={stroke} strokeWidth={Math.max(1, sw * 0.6)} />
          <line x1={hw-ins} y1={-hh} x2={hw-ins} y2={hh} stroke={stroke} strokeWidth={Math.max(1, sw * 0.6)} />
        </>
      );
    }
    case "cylinder": {
      const ey = Math.min(8, hh * 0.25);
      const topY = -hh + ey;
      const botY = hh - ey;
      return (
        <>
          <path
            d={`M${-hw},${topY} A${hw},${ey} 0 0,1 ${hw},${topY} L${hw},${botY} A${hw},${ey} 0 0,1 ${-hw},${botY} Z`}
            fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={sd}
          />
          <ellipse cx={0} cy={topY} rx={hw} ry={ey} fill={fill} stroke={stroke} strokeWidth={sw} />
        </>
      );
    }
    case "flag": {
      const indent = Math.min(14, hw * 0.18);
      return <polygon points={`${-hw},${-hh} ${hw},${-hh} ${hw},${hh} ${-hw},${hh} ${-hw+indent},0`} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={sd} />;
    }
    default:
      return <rect x={-hw} y={-hh} width={W} height={H} rx={6} ry={6} fill={fill} stroke={stroke} strokeWidth={sw} strokeDasharray={sd} />;
  }
};

// Word-wrap text into lines fitting maxChars
const wrapText = (text, maxChars) => {
  if (!text) return [""];
  const words = String(text).split(/\s+/);
  const lines = [];
  let cur = "";
  for (const w of words) {
    if (!cur) { cur = w; continue; }
    if ((cur + " " + w).length <= maxChars) { cur += " " + w; }
    else { lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [""];
};

const LH = 13;

// Bottom inset for full-screen view (step navigator + zoom: ~80px).
// Inline mini view has no step navigator, only zoom controls (~52px).
const BOTTOM_TOOLBAR_INSET_FULL = 80;
const BOTTOM_TOOLBAR_INSET_INLINE = 52;

const TraversalGraph = forwardRef(({ gNodes, gEdges, nodeMap, curNid, activeNids, focusNodeId, focusKey = 0, eliminatedSet, fadedNids, canvasW, canvasH, typesUsed, GNODE_STYLES, R, hideToolbar, inline = false, hideTrace = false, mapTitle = null, pendingEdge = null, stepHops = null, emptyMessage = null }, ref) => {
  const BOTTOM_TOOLBAR_INSET = inline ? BOTTOM_TOOLBAR_INSET_INLINE : BOTTOM_TOOLBAR_INSET_FULL;
  const activeSet = activeNids || new Set();
  const fadedSet = fadedNids || new Set();
  const focusMode = Boolean(focusNodeId);
  const containerRef = useRef(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdgeIdx, setSelectedEdgeIdx] = useState(null);
  const [currentStep, setCurrentStep] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchMatches, setSearchMatches] = useState([]);
  const [searchIndex, setSearchIndex] = useState(0);
  const searchInputRef = useRef(null);

  const [dragNode, setDragNode] = useState(null);
  const [nodePositions, setNodePositions] = useState({});
  const dragStartRef = useRef({ x: 0, y: 0, nx: 0, ny: 0 });
  // Tracks whether a drag actually moved the node (screen-space distance), so the
  // mouseup->click that follows a drag doesn't toggle the node's selection off.
  const dragMovedRef = useRef(false);
  // Node id just copied from the inspector panel — drives the tick feedback.
  const [copiedNodeId, setCopiedNodeId] = useState(null);
  const autoFocusedRef = useRef(false);
  // In inline mode: tracks which node gets the amber "targeted" ring; moves with step navigation
  const [localFocusTarget, setLocalFocusTarget] = useState(focusNodeId);

  /* ── Step model ────────────────────────────────────────────────────────────
     Two numbering systems are on the canvas at once and they must agree:
       blue badge on a NODE = its ordinal in the walk (1..N)
       red  badge on an EDGE = the step number (1..N-1)
     Step k is the hop from node k to node k+1. So the navigator counts HOPS
     (`stepHops`, from the snapshot builder), not the edges that happen to carry
     a badge: a hop whose connection cannot be drawn — a bridged backtrack, or
     the pair left behind when an unresolved id was dropped — has no edge, and
     counting edges both under-reported the total and let `›` dead-end on it.
     Declared here, above the focus effects that read it. */
  const stepEdges = gEdges.filter(e => e.step != null).sort((a, b) => a.step - b.step);
  // The node the walk LANDED on for this step. For an edge walked against its
  // stored direction (backtrack) that is `from`, not `to`.
  const stepTarget = (e) => (e.traversedReversed ? e.from : e.to);
  // Callers that supply no hops (the fallback trail graph) fall back to the
  // numbered edges — exactly the old behaviour.
  const hops = (stepHops && stepHops.length)
    ? [...stepHops].sort((a, b) => a.step - b.step)
    : stepEdges.map(e => ({ step: e.step, to: stepTarget(e) }));
  const maxStep = hops.length > 0 ? hops[hops.length - 1].step : 0;
  // Where step N focuses: the hop's destination when we know it, else the
  // numbered edge's target.
  const nodeForStep = useCallback((step) => {
    const hop = hops.find(h => h.step === step);
    if (hop && hop.to) return hop.to;
    const edge = stepEdges.find(e => e.step === step);
    return edge ? stepTarget(edge) : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepHops, gEdges]);
  // The step that LANDED on this node (the last one, so a revisit reports the
  // most recent arrival) — used to sync the counter to a focus/current node.
  const stepForNode = useCallback((nid) => {
    if (!nid) return null;
    for (let i = hops.length - 1; i >= 0; i--) if (hops[i].to === nid) return hops[i].step;
    const edge = stepEdges.find(e => stepTarget(e) === nid);
    return edge ? edge.step : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepHops, gEdges]);
  // The effects below run before `stepForNode` would be reachable by name in
  // their own dependency lists; a ref keeps them on the current closure without
  // re-running them on every render.
  const stepForNodeRef = useRef(stepForNode);
  stepForNodeRef.current = stepForNode;

  // Stable key — only resets dragged positions when the actual node set changes,
  // not on every re-render caused by parent recreating the gNodes array.
  const gNodeKey = gNodes.map(n => n.id).join('\x00');
  useEffect(() => {
    const pos = {};
    gNodes.forEach(nd => { pos[nd.id] = { x: nd.x, y: nd.y }; });
    setNodePositions(pos);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gNodeKey]);

  useEffect(() => {
    const blockBrowserZoom = (e) => { if (e.ctrlKey || e.metaKey) e.preventDefault(); };
    const blockKeyZoom = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "+" || e.key === "-" || e.key === "=" || e.key === "0")) e.preventDefault();
      if ((e.ctrlKey || e.metaKey) && e.key === "f") { e.preventDefault(); searchInputRef.current?.focus(); }
    };
    document.addEventListener("wheel", blockBrowserZoom, { passive: false });
    document.addEventListener("keydown", blockKeyZoom);
    return () => { document.removeEventListener("wheel", blockBrowserZoom); document.removeEventListener("keydown", blockKeyZoom); };
  }, []);

  useEffect(() => {
    if (!containerRef.current || gNodes.length === 0 || focusNodeId) return;
    // Full map with a known curNid: let the auto-focus effect handle it — but
    // ONLY when the node actually exists in the layout. Otherwise fall through
    // to fitView so the canvas is never blank.
    if (curNid && !inline && nodePositions[curNid]) return;
    const doFit = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || rect.width < 50 || rect.height < 50) return;
      if (gNodes.length === 0) return;
      // Use dagre layout positions (gNodes.x/y) — NOT nodePositions so drag events
      // don't retrigger fitView and jump the viewport on every mousemove.
      const pad = 80;
      const minX = Math.min(...gNodes.map(n => n.x)) - pad;
      const maxX = Math.max(...gNodes.map(n => n.x)) + pad;
      const minY = Math.min(...gNodes.map(n => n.y)) - pad;
      const maxY = Math.max(...gNodes.map(n => n.y)) + pad;
      const w = maxX - minX || 600;
      const h = maxY - minY || 400;
      const usableH = rect.height - BOTTOM_TOOLBAR_INSET;
      const sx = (rect.width - 40) / w;
      const sy = (usableH - 40) / h;
      const s = Math.max(Math.min(sx, sy, 1.5), 0.1);
      setTransform({ x: (rect.width - w * s) / 2 - minX * s, y: (usableH - h * s) / 2 - minY * s, scale: s });
    };
    doFit();
    const t1 = requestAnimationFrame(doFit);
    const t2 = setTimeout(doFit, 100);
    const t3 = setTimeout(doFit, 300);
    return () => { cancelAnimationFrame(t1); clearTimeout(t2); clearTimeout(t3); };
  // nodePositions intentionally excluded — drag must not retrigger fitView
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gNodes.length, canvasW, canvasH, focusNodeId, curNid]);

  useEffect(() => {
    if (!focusNodeId || !containerRef.current || Object.keys(nodePositions).length === 0) return;
    const doFocus = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || rect.width < 50 || rect.height < 50) return;
      if (inline) {
        // Fit all neighborhood nodes (gNodes is already the filtered subset)
        const pad = 60;
        const xs = gNodes.map(n => nodePositions[n.id]?.x ?? n.x);
        const ys = gNodes.map(n => nodePositions[n.id]?.y ?? n.y);
        const minX = Math.min(...xs) - pad;
        const maxX = Math.max(...xs) + pad;
        const minY = Math.min(...ys) - pad;
        const maxY = Math.max(...ys) + pad;
        const w = maxX - minX || 300;
        const h = maxY - minY || 300;
        const usableH = rect.height - BOTTOM_TOOLBAR_INSET;
        const s = Math.max(Math.min((rect.width - 20) / w, (usableH - 20) / h, 2), 0.15);
        setTransform({ x: (rect.width - w * s) / 2 - minX * s, y: (usableH - h * s) / 2 - minY * s, scale: s });
      } else {
        const targetId = nodePositions[focusNodeId] ? focusNodeId : (curNid && nodePositions[curNid] ? curNid : null);
        const pos = targetId && nodePositions[targetId];
        if (!pos) return;
        setTransform({ x: rect.width / 2 - pos.x * 1.5, y: (rect.height - BOTTOM_TOOLBAR_INSET) / 2 - pos.y * 1.5, scale: 1.5 });
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(doFocus));
    // Sync step counter + amber ring to focusNodeId (don't auto-open panel in inline)
    const focusStep = stepForNodeRef.current(focusNodeId);
    if (focusStep != null) { setCurrentStep(focusStep); if (!inline) setSelectedNode(focusNodeId); }
    if (inline) setLocalFocusTarget(focusNodeId);
  }, [focusNodeId, focusKey, nodePositions, curNid, inline, gNodes, gEdges]);

  // On open (mount), auto-focus curNid for full map only (not inline/mini map).
  // Fires at 350ms to guarantee it runs after fitView's 300ms delayed timer.
  useEffect(() => {
    if (inline) return; // inline map uses focusNodeId effect for its own fit-all positioning
    if (focusNodeId) return;
    if (!curNid || Object.keys(nodePositions).length === 0 || !containerRef.current) return;
    if (autoFocusedRef.current) return;
    autoFocusedRef.current = true;
    const pos = nodePositions[curNid];
    if (!pos) return;
    const doFocus = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || rect.width < 50 || rect.height < 50) return;
      setTransform({ x: rect.width / 2 - pos.x, y: (rect.height - BOTTOM_TOOLBAR_INSET) / 2 - pos.y, scale: 1 });
      const st = stepForNodeRef.current(curNid);
      if (st != null) { setCurrentStep(st); if (!inline) setSelectedNode(curNid); }
    };
    const t = setTimeout(doFocus, 350);
    return () => clearTimeout(t);
  }, [nodePositions, curNid, focusNodeId, gEdges, inline]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const d = e.deltaY > 0 ? 0.92 : 1.08;
    setTransform(prev => {
      const ns = Math.min(Math.max(prev.scale * d, 0.1), 3);
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return { ...prev, scale: ns };
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      return { x: cx - (cx - prev.x) * (ns / prev.scale), y: cy - (cy - prev.y) * (ns / prev.scale), scale: ns };
    });
  }, []);

  // Bound natively rather than via React's onWheel: React registers wheel at the
  // root as a *passive* listener, so preventDefault() inside a synthetic handler is
  // rejected and logs an error on every wheel tick.
  //
  // Attached through a ref CALLBACK, not a mount-effect. handleWheel is a
  // useCallback([]) so it never changes identity, which meant an effect keyed on it
  // ran exactly once: if the node was not mounted on that pass — or was later
  // replaced — the listener was never attached and wheel/trackpad zoom silently did
  // nothing, while the +/- buttons kept working because zoomBy reads the ref lazily.
  // A ref callback fires on every attach and detach, so the binding cannot be missed.
  const setContainerRef = useCallback((el) => {
    const prev = containerRef.current;
    if (prev === el) return;
    if (prev) prev.removeEventListener("wheel", handleWheel);
    containerRef.current = el;
    if (el) el.addEventListener("wheel", handleWheel, { passive: false });
  }, [handleWheel]);

  const zoomBy = useCallback((factor) => {
    setTransform(prev => {
      const rect = containerRef.current?.getBoundingClientRect();
      const ns = Math.min(Math.max(prev.scale * factor, 0.05), 3);
      if (!rect) return { ...prev, scale: ns };
      const cx = rect.width / 2, cy = rect.height / 2;
      return { x: cx - (cx - prev.x) * (ns / prev.scale), y: cy - (cy - prev.y) * (ns / prev.scale), scale: ns };
    });
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0 || dragNode) return;
    e.preventDefault(); // stops browser text-selection on SVG text during canvas pan
    setIsPanning(true);
    setSelectedEdgeIdx(null);
    setSelectedNode(null); // click on empty canvas deselects any selected node
    panStart.current = { x: e.clientX, y: e.clientY, tx: transform.x, ty: transform.y };
  }, [transform, dragNode]);

  const handleMouseMove = useCallback((e) => {
    if (dragNode) {
      if (!dragMovedRef.current) {
        // Screen-space distance, not graph-space — 4px must mean 4px regardless of zoom.
        const sdx = e.clientX - dragStartRef.current.x;
        const sdy = e.clientY - dragStartRef.current.y;
        if (sdx * sdx + sdy * sdy > 16) dragMovedRef.current = true;
      }
      const dx = (e.clientX - dragStartRef.current.x) / transform.scale;
      const dy = (e.clientY - dragStartRef.current.y) / transform.scale;
      setNodePositions(prev => ({ ...prev, [dragNode]: { x: dragStartRef.current.nx + dx, y: dragStartRef.current.ny + dy } }));
      return;
    }
    if (!isPanning) return;
    setTransform(prev => ({
      ...prev,
      x: panStart.current.tx + (e.clientX - panStart.current.x),
      y: panStart.current.ty + (e.clientY - panStart.current.y),
    }));
  }, [isPanning, dragNode, transform.scale]);

  const handleMouseUp = useCallback(() => { setIsPanning(false); setDragNode(null); }, []);

  const startNodeDrag = useCallback((e, nodeId) => {
    e.preventDefault(); // stops text selection on SVG text during node drag
    e.stopPropagation();
    setDragNode(nodeId);
    dragMovedRef.current = false;
    const pos = nodePositions[nodeId] || { x: 0, y: 0 };
    dragStartRef.current = { x: e.clientX, y: e.clientY, nx: pos.x, ny: pos.y };
  }, [nodePositions]);

  const fitView = useCallback(() => {
    if (!containerRef.current || gNodes.length === 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.width < 50 || rect.height < 50) return;
    // Center on visited/active nodes at 100% scale
    const relevant = gNodes.filter(n => n.visited !== false);
    const nodes = relevant.length > 0 ? relevant : gNodes;
    let sumX = 0, sumY = 0;
    nodes.forEach(nd => {
      const pos = nodePositions[nd.id] || { x: nd.x, y: nd.y };
      sumX += pos.x; sumY += pos.y;
    });
    const cx = sumX / nodes.length;
    const cy = sumY / nodes.length;
    setTransform({ x: rect.width / 2 - cx, y: (rect.height - BOTTOM_TOOLBAR_INSET) / 2 - cy, scale: 1 });
  }, [gNodes, nodePositions]);

  const panToNode = useCallback((nodeId) => {
    if (!containerRef.current) return;
    const pos = nodePositions[nodeId];
    if (!pos) return;
    const rect = containerRef.current.getBoundingClientRect();
    const scale = Math.max(transform.scale, 0.6);
    setTransform({ x: rect.width / 2 - pos.x * scale, y: (rect.height - BOTTOM_TOOLBAR_INSET) / 2 - pos.y * scale, scale });
  }, [nodePositions, transform.scale]);

  const goToStep = useCallback((step) => {
    if (step < 1 || step > maxStep || !containerRef.current) return;
    const target = nodeForStep(step);
    // The step always advances, even when its node is not on this canvas
    // (hidden type, inline neighbourhood): navigation must never dead-end.
    setCurrentStep(step);
    if (!target) return;
    if (inline) {
      setLocalFocusTarget(target); // move amber ring to this step's node
    } else {
      setSelectedNode(target);     // open detail panel only in full map
    }
    const targetPos = nodePositions[target];
    if (!targetPos) return;
    const rect = containerRef.current.getBoundingClientRect();
    const scale = Math.max(transform.scale, 0.8);
    setTransform({ x: rect.width / 2 - targetPos.x * scale, y: (rect.height - BOTTOM_TOOLBAR_INSET) / 2 - targetPos.y * scale, scale });
  }, [nodeForStep, maxStep, nodePositions, transform.scale, inline]);

  // Pan to a step's node without changing currentStep (used by "last visited" button)
  const panToStep = useCallback((step) => {
    const target = nodeForStep(step);
    if (!target || !containerRef.current) return;
    const targetPos = nodePositions[target];
    if (!targetPos) return;
    const rect = containerRef.current.getBoundingClientRect();
    const scale = Math.max(transform.scale, 0.8);
    setTransform({ x: rect.width / 2 - targetPos.x * scale, y: (rect.height - BOTTOM_TOOLBAR_INSET) / 2 - targetPos.y * scale, scale });
  }, [nodeForStep, nodePositions, transform.scale]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (!query.trim()) { setSearchMatches([]); setSearchIndex(0); return; }
    const q = query.toLowerCase();
    const matches = gNodes.filter(
      (n) => n.id.toLowerCase().includes(q) || n.name.toLowerCase().includes(q) || n.type.toLowerCase().includes(q)
    ).map((n) => n.id);
    setSearchMatches(matches);
    setSearchIndex(0);
    if (matches.length > 0) panToNode(matches[0]);
  }, [gNodes, panToNode]);

  const handleSearchNav = useCallback((dir) => {
    if (searchMatches.length === 0) return;
    const next = (searchIndex + dir + searchMatches.length) % searchMatches.length;
    setSearchIndex(next);
    panToNode(searchMatches[next]);
  }, [searchMatches, searchIndex, panToNode]);

  useImperativeHandle(ref, () => ({
    fitView,
    getScale: () => transform.scale,
    search: handleSearch,
    searchNav: handleSearchNav,
    getSearchState: () => ({ searchQuery, searchMatches, searchIndex }),
    focusSearch: () => searchInputRef.current?.focus(),
    panToNode,
    selectNode: (id) => setSelectedNode(id),
    // Exposed for the Trace Path panel so a row click drives the SAME step
    // navigation the bottom trace bar uses — counter, selection and pan stay in
    // one place instead of a second focus path that could drift.
    goToStep,
  }), [fitView, transform.scale, handleSearch, handleSearchNav, searchQuery, searchMatches, searchIndex, panToNode, goToStep]);

  const getStyle = (type) => GNODE_STYLES[type] || GNODE_STYLES._default;
  const getPos = (id) => nodePositions[id] || { x: 0, y: 0 };

  const computeEdgePath = (fromPos, toPos, fromNd, toNd) => {
    const fW = fromNd.W || 180, fH = fromNd.H || 44;
    const tW = toNd.W || 180, tH = toNd.H || 44;
    const cx = fromPos.x, cy = fromPos.y;
    const tx2 = toPos.x, ty2 = toPos.y;
    const dx = tx2 - cx, dy = ty2 - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 1) return { x1: cx, y1: cy, x2: tx2, y2: ty2, path: `M${cx},${cy}L${tx2},${ty2}`, midX: cx, midY: cy };
    const v = Math.min(Math.max(0.12 * dist, 8), 30);

    // Direction-aware exit/entry so dragged nodes route correctly in any direction
    let srcX, srcY, exitX, exitY, entX, entY, tgtX, tgtY;
    if (Math.abs(dy) >= Math.abs(dx) * 0.5) {
      // Mostly vertical
      if (dy >= 0) {
        // Source above target: exit bottom, enter top
        srcX = cx;   srcY = cy  + fH / 2;
        exitX = srcX; exitY = srcY + v;
        tgtX = tx2;  tgtY = ty2 - tH / 2 - 3;
        entX = tgtX; entY = tgtY - v;
      } else {
        // Source below target: exit top, enter bottom
        srcX = cx;   srcY = cy  - fH / 2;
        exitX = srcX; exitY = srcY - v;
        tgtX = tx2;  tgtY = ty2 + tH / 2 + 3;
        entX = tgtX; entY = tgtY + v;
      }
    } else {
      // Mostly horizontal
      if (dx >= 0) {
        // Source left of target: exit right, enter left
        srcX = cx  + fW / 2; srcY = cy;
        exitX = srcX + v;    exitY = srcY;
        tgtX = tx2 - tW / 2 - 3; tgtY = ty2;
        entX = tgtX - v;     entY = tgtY;
      } else {
        // Source right of target: exit left, enter right
        srcX = cx  - fW / 2; srcY = cy;
        exitX = srcX - v;    exitY = srcY;
        tgtX = tx2 + tW / 2 + 3; tgtY = ty2;
        entX = tgtX + v;     entY = tgtY;
      }
    }

    const path = `M ${srcX} ${srcY} L ${exitX} ${exitY} L ${entX} ${entY} L ${tgtX} ${tgtY}`;
    return { x1: srcX, y1: srcY, x2: tgtX, y2: tgtY, path, midX: (exitX + entX) / 2, midY: (exitY + entY) / 2 };
  };

  if (gNodes.length === 0) {
    // The default text describes a diagnosis in progress. A standalone source map has
    // no triage behind it, so the owner passes the wording that is true there instead.
    const empty = emptyMessage || {
      headline: "No graph data yet",
      detail: "Graph will appear once triage identifies candidate paths",
    };
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400 text-sm bg-white" style={{ fontFamily: SG }}>
        <div className="text-center">
          <p className="text-base font-semibold text-[#253439] mb-1">{empty.headline}</p>
          <p className="text-xs text-gray-400">{empty.detail}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden bg-white" style={{ fontFamily: SG }}>
      {!hideToolbar && (
        <div className="flex-shrink-0 flex items-center justify-end px-3 py-1.5 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-1.5">
            <div className="relative flex items-center">
              <svg className="absolute left-2 w-3 h-3 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); handleSearchNav(e.shiftKey ? -1 : 1); }
                  if (e.key === "Escape") { setSearchQuery(""); setSearchMatches([]); setSearchIndex(0); searchInputRef.current?.blur(); }
                }}
                placeholder="Search nid / name..."
                className="w-40 pl-7 pr-6 py-1 text-[10px] bg-white border border-gray-200 rounded-md outline-none focus:border-[#06b6d4] focus:ring-1 focus:ring-cyan-200 transition-all placeholder-gray-400 text-[#253439]"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(""); setSearchMatches([]); setSearchIndex(0); searchInputRef.current?.focus(); }}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
                >
                  <svg width="8" height="8" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
                </button>
              )}
              {searchMatches.length > 0 && (
                <div className="flex items-center gap-0.5 ml-1">
                  <span className="text-[9px] text-[#253439] font-mono tabular-nums whitespace-nowrap">
                    {searchIndex + 1}/{searchMatches.length}
                  </span>
                  <button onClick={() => handleSearchNav(-1)} className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-[#06b6d4] rounded hover:bg-cyan-50 transition">
                    <svg width="8" height="8" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6" /></svg>
                  </button>
                  <button onClick={() => handleSearchNav(1)} className="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-[#06b6d4] rounded hover:bg-cyan-50 transition">
                    <svg width="8" height="8" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6" /></svg>
                  </button>
                </div>
              )}
              {searchQuery && searchMatches.length === 0 && (
                <span className="ml-1 text-[9px] text-red-400 whitespace-nowrap">No match</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Wrapper: relative but NO overflow-hidden so step navigator can overflow without clipping */}
      <div className="flex-1 relative min-h-0">
      <div
        ref={setContainerRef}
        className="absolute inset-0 overflow-hidden"
        style={{ cursor: dragNode ? "grabbing" : isPanning ? "grabbing" : "grab", userSelect: (dragNode || isPanning) ? "none" : "auto", background: "radial-gradient(circle, #e2e8f0 1px, transparent 1px)", backgroundColor: "#f8fafc", backgroundSize: "20px 20px" }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <svg width="100%" height="100%" style={{ position: "absolute", top: 0, left: 0 }}>
          <defs>
            <filter id="node-shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#000000" floodOpacity="0.25" />
            </filter>
            <filter id="node-shadow-hover" x="-25%" y="-25%" width="150%" height="150%">
              <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="#000000" floodOpacity="0.3" />
            </filter>
            {/* Exact markers from Mermaid Explorer (localhost:7842) */}
            <marker id="rf-arrow-default-light" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
              <polygon points="0 0, 8 3, 0 6" fill="#94a3b8" />
            </marker>
            <marker id="rf-arrow-cyan" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
              <polygon points="0 0, 8 3, 0 6" fill="#22d3ee" />
            </marker>
            <marker id="rf-arrow-amber" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
              <polygon points="0 0, 8 3, 0 6" fill="#f59e0b" />
            </marker>
            <marker id="rf-arrow-dashed-light" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
              <polygon points="0 0, 8 3, 0 6" fill="#64748b" />
            </marker>
            <marker id="rf-arrow-thick" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
              <polygon points="0 0, 8 3, 0 6" fill="#60a5fa" />
            </marker>
            <marker id="rf-arrow-red" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
              <polygon points="0 0, 8 3, 0 6" fill="#ef4444" />
            </marker>
            <marker id="rf-arrow-green" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
              <polygon points="0 0, 8 3, 0 6" fill="#22c55e" />
            </marker>
            <marker id="rf-arrow-bridge" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
              <polygon points="0 0, 8 3, 0 6" fill="#f87171" />
            </marker>
            <marker id="rf-arrow-pending" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
              <polygon points="0 0, 8 3, 0 6" fill="#ef4444" opacity="0.55" />
            </marker>
            <style>{`
              @keyframes pending-edge-pulse {
                0%, 100% { opacity: 0.35; }
                50% { opacity: 0.7; }
              }
              .pending-edge-pulse { animation: pending-edge-pulse 1.6s ease-in-out infinite; }
            `}</style>
          </defs>
          <g transform={`translate(${transform.x},${transform.y}) scale(${transform.scale})`}>
            {/* Edges */}
            {gEdges.map((e, ei) => {
              const fromNd = gNodes.find(n => n.id === e.from);
              const toNd = gNodes.find(n => n.id === e.to);
              if (!fromNd || !toNd) return null;
              // Bridge-only edge: on a return route but not forward-traversed.
              // Forward-traversed edges that also lie on a bridge keep traversed styling.
              const isBridge = e.bridge && !e.traversed;
              // For reverse-walked edges compute the path in walk direction so the
              // arrowhead points the way the agent actually walked. Two sources:
              // bridge routes (return paths) and traversal hops the walk took
              // against the stored edge direction (backtracks). Styling is
              // unaffected — a reversed traversal hop stays a normal traversed edge.
              const drawReversed = e.traversed
                ? Boolean(e.traversedReversed)
                : Boolean(isBridge && e.bridgeReversed);
              const pathSrc = drawReversed ? toNd : fromNd;
              const pathDst = drawReversed ? fromNd : toNd;
              const fromPos = getPos(pathSrc.id);
              const toPos = getPos(pathDst.id);
              const ep = computeEdgePath(fromPos, toPos, pathSrc, pathDst);
              const isFocusedEdge = focusMode && (e.from === focusNodeId || e.to === focusNodeId);
              const isTraversed = Boolean(e.traversed);
              const isActive = isTraversed && (e.to === curNid || e.from === curNid);
              const isOutgoingFromVisited = fromNd?.visited !== false && toNd?.visited === false && !isActive;
              const isSelectedEdge = selectedNode && (e.from === selectedNode || e.to === selectedNode);
              const fromProblemToVisited = fromNd?.type === "Problem" && toNd?.visited !== false;
              const isEliminated = eliminatedSet.has(e.to) || eliminatedSet.has(e.from);

              const isClickedEdge = selectedEdgeIdx === ei;
              const isU = isClickedEdge || isSelectedEdge;
              const isD = isActive;
              const isP = hoveredNode != null && hoveredNode !== e.from && hoveredNode !== e.to;
              // Optimistic pending-answer edge: matches by from+to but only when
              // the edge is not already traversed (real state always wins).
              const isPending = !isTraversed && !isBridge && pendingEdge
                && e.from === pendingEdge.from && e.to === pendingEdge.to;
              // Only explicitly traversed edges get colored; bridge edges get a lighter red dashed treatment
              const edgeColor = isEliminated ? "#b91c1c" : isBridge ? "#f87171" : isTraversed ? "#ef4444" : isPending ? "#ef4444" : isU ? "#f59e0b" : fromProblemToVisited ? "#ef4444" : "#94a3b8";
              const edgeWidth = isU ? 2 : isBridge ? 1.5 : isTraversed ? 2.5 : isPending ? 2.5 : 1;
              const edgeDash = isBridge ? "5 3" : isPending ? "6 4" : (isTraversed || isU) ? undefined : "6 4";
              // Markers
              let marker;
              if (isEliminated) marker = "url(#rf-arrow-red)";
              else if (isBridge) marker = "url(#rf-arrow-bridge)";
              else if (isTraversed) marker = "url(#rf-arrow-red)";
              else if (isPending) marker = "url(#rf-arrow-pending)";
              else if (isU) marker = "url(#rf-arrow-amber)";
              else marker = "url(#rf-arrow-dashed-light)";

              const midX = ep.midX;
              const midY = ep.midY;
              // Opacity — exact Mermaid Explorer: 0.08 when another node hovered, 1 otherwise
              const edgeOpacity = isP ? 0.08 : 1;
              const labelOpacity = isP ? 0.08 : 1;
              return (
                <g key={ei} className={isPending ? "pending-edge-pulse" : undefined} style={{ cursor: "pointer", transition: "opacity 0.15s" }} opacity={edgeOpacity} onClick={(ev) => { ev.stopPropagation(); setSelectedEdgeIdx(isClickedEdge ? null : ei); setSelectedNode(null); }}>
                  {/* Invisible wide hit area for easy clicking */}
                  <path d={ep.path} fill="none" stroke="transparent" strokeWidth={12} />
                  <path
                    d={ep.path}
                    fill="none"
                    stroke={edgeColor}
                    strokeWidth={edgeWidth}
                    strokeDasharray={edgeDash}
                    markerEnd={marker}
                    style={{ transition: "stroke 0.15s, stroke-width 0.15s" }}
                  />
                  {e.step != null && (
                    <g>
                      <circle cx={midX} cy={midY} r="9" fill={isTraversed ? "#ef4444" : "#3b82f6"} stroke="white" strokeWidth="1.5" />
                      <text x={midX} y={midY} textAnchor="middle" dominantBaseline="central" fontSize="8" fontWeight="bold" fill="white" fontFamily={SG}>
                        {e.step}
                      </text>
                    </g>
                  )}
                  {e.condition && (() => {
                    const lbl = e.condition.length > 28 ? e.condition.slice(0, 27) + "…" : e.condition;
                    const lw = lbl.length * 5 + 12;
                    const ly = midY + (e.step != null ? 14 : 0);
                    return (
                      <g opacity={labelOpacity}>
                        {/* Light-theme pill — matches Mermaid Explorer bg-white/90 text-slate-500 border border-slate-200 */}
                        <rect x={midX - lw / 2} y={ly - 7} width={lw} height={14} rx="3" fill="rgba(255,255,255,0.95)" stroke="#cbd5e1" strokeWidth="1" />
                        <text x={midX} y={ly} textAnchor="middle" dominantBaseline="central" fontSize="9" fontWeight="600" fill="#1e293b" fontFamily={SG}>
                          {lbl}
                        </text>
                      </g>
                    );
                  })()}
                </g>
              );
            })}

            {/* Nodes */}
            {gNodes.map((nd) => {
              const s = getStyle(nd.type);
              const pos = getPos(nd.id);
              const { shape, W, H } = nd;
              const label = nd.label || nd.name || nd.id;
              const isDiamond = shape === "diamond";
              const isHex = shape === "hexagon";
              const hw = W / 2, hh = H / 2;
              const PAD = 0.08;
              // Derive text box from shape geometry — no magic aspect ratios
              let textAreaW, textAreaH;
              if (isDiamond) {
                // Rhombus inscribed rectangle optimum: W/2 × H/2 (analytical: a/hw + b/hh = 1, max 4ab)
                textAreaW = (W / 2) * (1 - PAD);
                textAreaH = (H / 2) * (1 - PAD);
              } else if (isHex) {
                // Hexagon has indent i at top/bottom corners (matches shape rendering)
                // At half-height b: usable half-width = hw - indent*(b/hh)
                const indent = Math.min(18, hw * 0.25);
                textAreaH = H * (1 - PAD * 2);
                textAreaW = (hw - indent * (textAreaH / 2) / hh) * 2 * (1 - PAD);
              } else {
                textAreaW = W * (1 - PAD);
                textAreaH = H * (1 - PAD * 1.5);
              }
              // 5.5px avg char width gives more chars per line → fuller text display
              const charsPerLine = Math.max(Math.floor(textAreaW / 5.5), 8);
              // Allow as many lines as the node height can accommodate
              const maxLines = Math.max(Math.floor(textAreaH / LH), 3);
              let lines = wrapText(label, charsPerLine);
              if (lines.length > maxLines) {
                lines = lines.slice(0, maxLines);
                const last = lines[maxLines - 1];
                lines[maxLines - 1] = last.length > charsPerLine - 2 ? last.slice(0, charsPerLine - 2) + "…" : last + "…";
              }
              const totalTextH = lines.length * LH;
              const textStartY = -totalTextH / 2;

              // In inline mini map: localFocusTarget moves with step navigation; curNid = "active node" — kept visually separate
              const isFocusTarget = inline && focusMode && nd.id === localFocusTarget;
              const isCurrent = focusMode
                ? (inline ? false : (nd.id === focusNodeId || nd.visited !== false))
                : nd.id === curNid || activeSet.has(nd.id);
              const isProblem = nd.type === "Problem";
              const isEliminated = eliminatedSet.has(nd.id);
              const isVisited = nd.visited !== false;
              const isHovered = hoveredNode === nd.id;
              const isSelected = selectedNode === nd.id;
              const isSearchMatch = searchMatches.includes(nd.id);
              const isActiveMatch = searchMatches[searchIndex] === nd.id;
              const isFutureNode = fadedSet.has(nd.id);
              const dimmed = (inline && focusMode)
                ? !isVisited && !isFocusTarget && nd.id !== curNid && !isSelected && !isSearchMatch
                : focusMode
                ? !isCurrent && !isSelected && !isSearchMatch
                : !isVisited && !isCurrent && !isProblem && !isSelected && !isSearchMatch;

              // Focus target keeps its normal type color — it's marked by the amber
              // border + pulsing ring below, not by a different fill.
              // `via` distinguishes explicitly-visited nodes from path-fill.
              // "passed_through" gets a lighter green; missing/absent `via`
              // (legacy snapshots) defaults to full visited green.
              const isPassedThrough = nd.via === "passed_through";
              const mainFill = dimmed ? s.bg + "4D" : (isVisited ? (isPassedThrough ? "#bbf7d0" : "#22c55e") : s.bg);
              const isActiveNode = nd.id === curNid || activeSet.has(nd.id);
              const mainStroke = isActiveNode && !isFocusTarget ? "#22d3ee" : isFocusTarget ? "#f59e0b" : isCurrent ? "#22d3ee" : isSelected ? "#f59e0b" : isEliminated ? "#ef4444" : isHovered ? "#22d3ee" : dimmed ? s.border + "99" : (isPassedThrough ? "#86efac" : s.border);
              const mainSW = isFocusTarget ? 4 : isActiveNode ? 3.5 : isCurrent ? 2.5 : isSelected ? 2 : isHovered ? 2 : 1.5;
              const mainSD = isEliminated ? "6,4" : dimmed ? "4,4" : (isPassedThrough && !isHovered && !isSelected ? "5,3" : "");
              const textFill = dimmed ? s.border : (isPassedThrough ? "#166534" : s.text);

              return (
                <g
                  key={nd.id}
                  transform={`translate(${pos.x},${pos.y})`}
                  onMouseEnter={() => setHoveredNode(nd.id)}
                  onMouseLeave={() => setHoveredNode(null)}
                  onClick={(e) => { e.stopPropagation(); if (dragMovedRef.current) return; setSelectedNode(isSelected ? null : nd.id); setSelectedEdgeIdx(null); }}
                  onMouseDown={(e) => startNodeDrag(e, nd.id)}
                  style={{ cursor: dragNode === nd.id ? "grabbing" : "pointer", opacity: isFutureNode ? 0.25 : isEliminated ? 0.5 : 1, transition: "opacity 0.15s, filter 0.15s" }}
                  filter={dimmed ? "none" : isHovered ? "url(#node-shadow-hover)" : "url(#node-shadow)"}
                >
                  {/* Search highlight ring */}
                  {isActiveMatch && (
                    <g>
                      <animate attributeName="opacity" values="0.7;0.3;0.7" dur="1.2s" repeatCount="indefinite" />
                      {renderShape(shape, W + 12, H + 12, "none", "#fbbf24", 2.5, "")}
                    </g>
                  )}
                  {isSearchMatch && !isActiveMatch && (
                    <g opacity={0.4}>{renderShape(shape, W + 10, H + 10, "none", "#fbbf24", 2, "4,2")}</g>
                  )}

                  {/* Active current node — simple pulsing border */}
                  {isActiveNode && !dimmed && (
                    <g>
                      <animate attributeName="opacity" values="1;0.2;1" dur="1.6s" repeatCount="indefinite" />
                      {renderShape(shape, W + 10, H + 10, "none", "#22d3ee", 3, "")}
                    </g>
                  )}
                  {/* Current in focus mode — static ring */}
                  {isCurrent && !isActiveNode && !dimmed && (
                    <g>{renderShape(shape, W + 8, H + 8, "none", "#22d3ee", 2.5, "")}</g>
                  )}
                  {/* Focus target in inline mini map — amber pulsing ring, always visible */}
                  {isFocusTarget && !dimmed && (
                    <g>
                      <animate attributeName="opacity" values="1;0.3;1" dur="1.8s" repeatCount="indefinite" />
                      {renderShape(shape, W + 16, H + 16, "none", "#f59e0b", 3.5, "")}
                    </g>
                  )}
                  {/* Selected — amber outline offset */}
                  {isSelected && !isCurrent && (
                    <g>{renderShape(shape, W + 8, H + 8, "none", "#fbbf24", 2.5, "")}</g>
                  )}

                  {/* Main shape — solid fill, thin border, modern feel */}
                  {renderShape(shape, W, H, mainFill, mainStroke, mainSW, mainSD)}

                  {/* Clip text to inscribed text area — prevents overflow for all shapes */}
                  <defs>
                    <clipPath id={`clip-${nd.id}`}>
                      <rect x={-textAreaW / 2} y={-textAreaH / 2} width={textAreaW} height={textAreaH} />
                    </clipPath>
                  </defs>

                  {/* Text label — wrapped, centered, clipped to shape */}
                  <g clipPath={`url(#clip-${nd.id})`}>
                    {lines.map((line, i) => (
                      <text
                        key={i}
                        x={0}
                        y={textStartY + i * LH + LH / 2}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize="10"
                        fontWeight="600"
                        fill={textFill}
                        fontFamily={SG}
                      >
                        {line}
                      </text>
                    ))}
                  </g>

                  {/* Turn badge */}
                  {nd.turnVisited && (
                    <g transform={`translate(${W / 2 - 2}, ${-H / 2 - 2})`}>
                      <circle r="8" fill={isCurrent ? "#06b6d4" : "#3b82f6"} stroke="white" strokeWidth="1.5" />
                      <text textAnchor="middle" dominantBaseline="central" fontSize="7" fontWeight="bold" fill="white" fontFamily={SG}>
                        {nd.turnVisited}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}
          </g>
        </svg>

        {/* Hover tooltip */}
        {hoveredNode && !dragNode && (() => {
          const nd = gNodes.find(n => n.id === hoveredNode);
          if (!nd) return null;
          const pos = getPos(nd.id);
          const s = getStyle(nd.type);
          const isCurrent = nd.id === curNid || activeSet.has(nd.id);
          const isEliminated = eliminatedSet.has(nd.id);
          return (
            <div
              className="absolute bg-white border border-cyan-100 rounded-xl shadow-lg px-3 py-2.5 pointer-events-none z-50"
              style={{
                left: pos.x * transform.scale + transform.x + (nd.W || 100) / 2 * transform.scale + 12,
                top: pos.y * transform.scale + transform.y - 20,
                maxWidth: 300,
              }}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <div className="w-3 h-3 rounded-full border-2 shrink-0" style={{ backgroundColor: s.bg, borderColor: s.border }} />
                <span className="text-[11px] font-bold text-[#253439]">{nd.name}</span>
              </div>
              <p className="text-[9px] font-mono text-gray-400 break-all mb-1">{nd.id}</p>
              {nd.description && <p className="text-[9px] text-gray-500 mt-0.5 line-clamp-4">{nd.description}</p>}
              <div className="flex items-center gap-1.5 flex-wrap mt-1">
                <span className="text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ backgroundColor: s.bg, color: s.text, border: `1px solid ${s.border}` }}>{nd.type}</span>
                {nd.type === "Problem" && <span className="text-[9px] font-bold text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">ROOT</span>}
                {isCurrent && <span className="text-[9px] font-bold text-[#06b6d4] bg-cyan-50 border border-cyan-200 rounded px-1.5 py-0.5">CURRENT</span>}
                {nd.visited !== false && nd.via !== "passed_through" && !isCurrent && <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">VISITED</span>}
                {nd.visited !== false && nd.via === "passed_through" && <span className="text-[9px] font-medium text-green-700 bg-green-50 border border-green-200 rounded px-1.5 py-0.5" style={{ borderStyle: "dashed" }}>PASSED THROUGH</span>}
                {nd.visited === false && <span className="text-[9px] text-gray-400 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">NOT VISITED</span>}
                {isEliminated && <span className="text-[9px] font-bold text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">ELIMINATED</span>}
              </div>
            </div>
          );
        })()}

        {/* Selected node detail panel */}
        {selectedNode && (() => {
          const nd = gNodes.find(n => n.id === selectedNode);
          if (!nd) return null;
          const s = getStyle(nd.type);
          const inEdges = gEdges.filter(e => e.to === nd.id);
          const outEdges = gEdges.filter(e => e.from === nd.id);
          return (
            <div className="absolute right-3 top-3 w-64 bg-white rounded-2xl overflow-hidden z-40 flex flex-col" style={{ maxHeight: "70vh", fontFamily: SG, boxShadow: "0 4px 24px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)" }} onMouseDown={(e) => e.stopPropagation()}>
              <div className="relative px-4 pt-3 pb-2.5 shrink-0">
                <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl" style={{ background: s.border }} />
                <div className="flex items-start justify-between mt-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="flex items-center justify-center w-6 h-6 rounded-lg text-[9px] font-bold text-white shrink-0" style={{ background: s.border }}>{(GNODE_STYLES[nd.type] || GNODE_STYLES._default).icon}</span>
                    <div className="min-w-0">
                      <p className="text-[10px] font-medium text-gray-400">{nd.type}</p>
                      <p className="text-[12px] font-bold text-[#253439] leading-snug break-words">{nd.name}</p>
                    </div>
                  </div>
                  <button onClick={() => setSelectedNode(null)} className="w-6 h-6 flex items-center justify-center rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition shrink-0 ml-1">
                    <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
                  </button>
                </div>
              </div>
              <div className="px-4 pb-3 space-y-2.5 overflow-y-auto hide-scrollbar">
                <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-2.5 py-1.5">
                  <span className="text-[9px] text-gray-400 shrink-0">ID</span>
                  <span className="text-[10px] font-mono text-[#253439] break-all">{nd.id}</span>
                  {/* Copy the RAW mermaid id — that string is what an engineer
                      pastes back into the runbook, so it must not be prettified. */}
                  <button
                    type="button"
                    title={copiedNodeId === nd.id ? "Copied" : "Copy ID"}
                    aria-label={copiedNodeId === nd.id ? "ID copied" : "Copy ID"}
                    onClick={async (ev) => {
                      ev.stopPropagation();
                      if (await copyTextToClipboard(nd.id)) {
                        setCopiedNodeId(nd.id);
                        setTimeout(() => setCopiedNodeId((c) => (c === nd.id ? null : c)), 1200);
                      }
                    }}
                    className={`w-5 h-5 flex items-center justify-center rounded shrink-0 ml-auto transition-colors ${copiedNodeId === nd.id ? "text-[#0e7490]" : "text-gray-400 hover:text-[#0e7490] hover:bg-cyan-50"}`}
                  >
                    {copiedNodeId === nd.id ? (
                      <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>
                    ) : (
                      <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>
                    )}
                  </button>
                </div>
                {nd.description && (
                  <p className="text-[10px] text-gray-500 leading-relaxed">{nd.description}</p>
                )}
                <div className="flex flex-wrap gap-1">
                  {nd.id === curNid && <span className="text-[9px] font-semibold text-white bg-[#0e7490] rounded-full px-2 py-0.5">Active node</span>}
                  {eliminatedSet.has(nd.id) && <span className="text-[9px] font-semibold text-white bg-red-500 rounded-full px-2 py-0.5">Eliminated</span>}
                </div>
                {inEdges.length > 0 && (
                  <div>
                    <p className="text-[9px] font-semibold text-gray-400 mb-1 flex items-center gap-1">
                      <svg width="8" height="8" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
                      Incoming ({inEdges.length})
                    </p>
                    <div className="space-y-0.5">
                      {inEdges.map((e, i) => {
                        const fn = nodeMap[e.from];
                        const fs = fn ? getStyle(fn.type) : null;
                        const visited = fn?.visited;
                        return (
                          <button
                            key={i}
                            onClick={(ev) => { ev.stopPropagation(); panToNode(e.from); setSelectedNode(e.from); }}
                            className="w-full text-left px-2 py-1.5 rounded-lg text-[10px] hover:bg-gray-50 flex items-center gap-2 transition-colors group"
                          >
                            {fs && <span className="w-2.5 h-2.5 rounded-full shrink-0 ring-2 ring-white" style={{ background: fs.border }} />}
                            <span className="truncate text-[#253439] group-hover:text-[#0e7490] transition-colors">{fn?.name || e.from}</span>
                            <span className={`ml-auto text-[8px] font-medium shrink-0 rounded px-1.5 py-0.5 ${visited ? "text-[#0e7490] bg-cyan-50" : "text-gray-400 bg-gray-50"}`}>{visited ? "TRAVERSED" : "NEXT"}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {outEdges.length > 0 && (
                  <div>
                    <p className="text-[9px] font-semibold text-gray-400 mb-1 flex items-center gap-1">
                      <svg width="8" height="8" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                      Outgoing ({outEdges.length})
                    </p>
                    <div className="space-y-0.5">
                      {outEdges.map((e, i) => {
                        const tn = nodeMap[e.to];
                        const ts = tn ? getStyle(tn.type) : null;
                        const visited = tn?.visited;
                        return (
                          <button
                            key={i}
                            onClick={(ev) => { ev.stopPropagation(); panToNode(e.to); setSelectedNode(e.to); }}
                            className="w-full text-left px-2 py-1.5 rounded-lg text-[10px] hover:bg-gray-50 flex items-center gap-2 transition-colors group"
                          >
                            {ts && <span className="w-2.5 h-2.5 rounded-full shrink-0 ring-2 ring-white" style={{ background: ts.border }} />}
                            <span className="truncate text-[#253439] group-hover:text-[#0e7490] transition-colors">{tn?.name || e.to}</span>
                            {e.condition && <span className="text-[8px] text-gray-400 shrink-0">{e.condition}</span>}
                            <span className={`ml-auto text-[8px] font-medium shrink-0 rounded px-1.5 py-0.5 ${visited ? "text-[#0e7490] bg-cyan-50" : "text-gray-400 bg-gray-50"}`}>{visited ? "TRAVERSED" : "NEXT"}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Minimap — hidden in inline/mini-map mode */}
        {gNodes.length > 0 && !inline && (() => {
          const MW = 240, MH = 150;
          const pad = 40;
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          gNodes.forEach(nd => {
            const p = getPos(nd.id);
            const hw = (nd.W || 200) / 2, hh = (nd.H || 64) / 2;
            if (p.x - hw < minX) minX = p.x - hw;
            if (p.y - hh < minY) minY = p.y - hh;
            if (p.x + hw > maxX) maxX = p.x + hw;
            if (p.y + hh > maxY) maxY = p.y + hh;
          });
          minX -= pad; minY -= pad; maxX += pad; maxY += pad;
          const gw = maxX - minX || 1, gh = maxY - minY || 1;
          const scaleM = Math.min(MW / gw, MH / gh);
          const contEl = containerRef.current;
          const cw = contEl ? contEl.clientWidth : canvasW;
          const ch = contEl ? contEl.clientHeight : canvasH;
          const vpLeft = (-transform.x / transform.scale - minX) * scaleM;
          const vpTop = (-transform.y / transform.scale - minY) * scaleM;
          const vpW = (cw / transform.scale) * scaleM;
          const vpH = (ch / transform.scale) * scaleM;

          return (
            <div
              className="absolute bottom-3 right-3 rounded-lg overflow-hidden z-40 flex flex-col"
              style={{ width: MW, background: "rgba(255,255,255,0.92)", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {mapTitle && (
                <div style={{ padding: "2px 8px", borderBottom: "1px solid #f1f5f9", fontSize: 9, color: "#475569", fontFamily: SG, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: "16px", background: "rgba(248,250,252,0.95)" }}>
                  {mapTitle}
                </div>
              )}
              <svg width={MW} height={MH}>
                {gNodes.map(nd => {
                  const p = getPos(nd.id);
                  const s = getStyle(nd.type);
                  const cx = (p.x - minX) * scaleM;
                  const cy = (p.y - minY) * scaleM;
                  const rw = Math.max((nd.W || 200) * scaleM * 0.5, 2);
                  const rh = Math.max((nd.H || 64) * scaleM * 0.5, 1.5);
                  return <rect key={nd.id} x={cx - rw / 2} y={cy - rh / 2} width={rw} height={rh} rx={1} fill={s.bg} opacity={nd.visited !== false ? 0.9 : 0.3} />;
                })}
                {gEdges.map((e, i) => {
                  const fn = gNodes.find(n => n.id === e.from);
                  const tn = gNodes.find(n => n.id === e.to);
                  if (!fn || !tn) return null;
                  const fp = getPos(e.from), tp = getPos(e.to);
                  return <line key={i} x1={(fp.x - minX) * scaleM} y1={(fp.y - minY) * scaleM} x2={(tp.x - minX) * scaleM} y2={(tp.y - minY) * scaleM} stroke="#94a3b8" strokeWidth={0.5} opacity={0.3} />;
                })}
                <rect x={vpLeft} y={vpTop} width={Math.max(vpW, 4)} height={Math.max(vpH, 3)} fill="rgba(6,182,212,0.08)" stroke="#06b6d4" strokeWidth={1.5} rx={2} />
              </svg>
            </div>
          );
        })()}

        {/* Zoom controls — always visible at bottom-left */}
        <div className="absolute bottom-3 left-3 flex items-center gap-0 bg-white border border-gray-200 rounded-xl shadow-lg z-40 overflow-hidden" style={{ fontFamily: SG }} onMouseDown={(e) => e.stopPropagation()}>
          <button
            onClick={() => zoomBy(1.25)}
            className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors text-base font-bold border-r border-gray-200"
            title="Zoom in"
          >+</button>
          <span className="px-2 text-[10px] font-mono text-gray-500 select-none min-w-[40px] text-center">{Math.round(transform.scale * 100)}%</span>
          <button
            onClick={() => zoomBy(0.8)}
            className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors text-base font-bold border-l border-gray-200"
            title="Zoom out"
          >−</button>
          <div className="w-px h-5 bg-gray-200 mx-0.5" />
          <button
            onClick={fitView}
            className="px-2.5 h-8 text-[10px] font-semibold text-[#06b6d4] hover:bg-cyan-50 hover:text-[#0891b2] transition-colors"
            title="Fit all nodes"
          >Fit</button>
        </div>

      </div>

      {/* Step navigator — full map only, outside overflow-hidden canvas so it never gets clipped */}
      {!inline && !hideTrace && maxStep > 0 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2.5 bg-white border border-cyan-200 rounded-xl shadow-lg z-40 px-4 py-2" style={{ fontFamily: SG }}>
          {!inline && (
            <>
              <div className="flex items-center gap-1.5">
                <svg width="12" height="12" fill="none" stroke="#06b6d4" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><path d="M22 4L12 14.01l-3-3" /></svg>
                <span className="text-[11px] font-semibold text-[#06b6d4] shrink-0">Trace Path</span>
              </div>
              <div className="w-px h-4 bg-cyan-200" />
            </>
          )}
          <div className="flex items-center gap-0 border border-cyan-200 rounded-lg overflow-hidden shrink-0">
            <button
              onClick={() => goToStep(Math.max((currentStep || 1) - 1, 1))}
              disabled={!currentStep || currentStep <= 1}
              className="px-2.5 py-1.5 text-[#06b6d4] hover:bg-cyan-50 disabled:text-gray-300 disabled:hover:bg-white transition-colors border-r border-cyan-200"
            >
              <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6" /></svg>
            </button>
            <button
              onClick={() => goToStep(currentStep || 1)}
              className="px-3 py-1.5 text-[10px] text-[#253439] font-medium tabular-nums text-center whitespace-nowrap bg-cyan-50/30 hover:bg-cyan-100 transition-colors"
              title="Focus current step"
            >
              {currentStep ? `Step ${currentStep} / ${maxStep}` : `Step 1 / ${maxStep}`}
            </button>
            <button
              onClick={() => goToStep(Math.min((currentStep || 0) + 1, maxStep))}
              disabled={currentStep >= maxStep}
              className="px-2.5 py-1.5 text-[#06b6d4] hover:bg-cyan-50 disabled:text-gray-300 disabled:hover:bg-white transition-colors border-l border-cyan-200"
            >
              <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6" /></svg>
            </button>
          </div>
          <div className="w-px h-4 bg-cyan-200" />
          <button
            onClick={() => {
              if (!curNid) return;
              panToNode(curNid);
              // Last hop that LANDED on curNid — hop-based, so a node reached
              // by an undrawable hop still syncs the step counter.
              const st = stepForNode(curNid);
              if (st != null) { setCurrentStep(st); if (!inline) setSelectedNode(curNid); }
            }}
            disabled={!curNid}
            title="Focus current node"
            className="p-1.5 rounded-lg text-[#06b6d4] hover:bg-cyan-50 disabled:text-gray-300 disabled:hover:bg-white transition-colors"
          >
            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
              <circle cx="12" cy="9" r="2.5" fill="currentColor" stroke="none" />
            </svg>
          </button>
        </div>
      )}
      </div>
    </div>
  );
});

export default TraversalGraph;
